import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  GripVertical,
  FileText,
  Video,
  BookOpen,
  Upload,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Calendar,
  Sparkles
} from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import ActivityContentBuilder from '../../components/admin/ActivityContentBuilder';

const API = `${API_BASE}/api`;

const ACTIVITY_TYPES = [
  { value: 'ai_course', label: 'AI Course (NAVI Generated)', icon: Sparkles, hasContentBuilder: true, isAI: true },
  { value: 'course', label: 'Course (Build Manually)', icon: BookOpen, hasContentBuilder: true },
  { value: 'existing_course', label: 'Course (Select Existing)', icon: BookOpen, hasContentBuilder: false },
  { value: 'ai_learning_lab', label: 'AI Learning Lab (NAVI Generated)', icon: Sparkles, hasContentBuilder: true, isAI: true },
  { value: 'learning_lab', label: 'Learning Lab (Build Manually)', icon: BookOpen, hasContentBuilder: true },
  { value: 'existing_lab', label: 'Learning Lab (Select Existing)', icon: BookOpen, hasContentBuilder: false },
  { value: 'video', label: 'Video', icon: Video, hasContentBuilder: false },
  { value: 'masterclass', label: 'Masterclass (Select Existing)', icon: Video, hasContentBuilder: false },
  { value: 'aicc', label: 'AICC / External', icon: ExternalLink, hasContentBuilder: false },
  { value: 'document', label: 'Document', icon: FileText, hasContentBuilder: false },
  { value: 'assignment_brief', label: 'Assignment Brief', icon: Upload, hasContentBuilder: false },
];

export default function ProgrammeBuilderPage() {
  const navigate = useNavigate();
  const { programmeId } = useParams();
  const { token } = useAuth();
  const isEditing = !!programmeId;

  const headers = { Authorization: `Bearer ${token}` };

  // Programme metadata
  const [metadata, setMetadata] = useState({
    programme_name: '',
    professional_body: '',
    designation: '',
    occupation: '',
    nqf_level: '',
    target_audience: '',
    entry_requirements: '',
    duration: '',
    credits: '',
    facilitator_name: '',
    moderator_name: '',
    start_date: '',
    end_date: '',
    structure_type: 'weeks',
    description: '',
    is_published: false
  });

  // Periods (weeks/months) with activities
  const [periods, setPeriods] = useState([]);
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);

  // Content selection for activities
  const [availableCourses, setAvailableCourses] = useState([]);
  const [availableLabs, setAvailableLabs] = useState([]);
  const [availableMasterclasses, setAvailableMasterclasses] = useState([]);

  useEffect(() => {
    fetchAvailableContent();
    if (isEditing) {
      fetchProgramme();
    }
  }, [programmeId]);

  const fetchAvailableContent = async () => {
    try {
      const [coursesRes, labsRes, masterclassesRes] = await Promise.all([
        axios.get(`${API}/courses`, { headers }),
        axios.get(`${API}/learning-labs`, { headers }),
        axios.get(`${API}/webinars`, { headers })
      ]);
      setAvailableCourses(coursesRes.data.courses || coursesRes.data || []);
      setAvailableLabs(labsRes.data.labs || labsRes.data || []);
      setAvailableMasterclasses(masterclassesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    }
  };

  const fetchProgramme = async () => {
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}`, { headers });
      const programme = res.data;
      
      setMetadata({
        programme_name: programme.programme_name || '',
        professional_body: programme.professional_body || '',
        designation: programme.designation || '',
        occupation: programme.occupation || '',
        nqf_level: programme.nqf_level || '',
        target_audience: programme.target_audience || '',
        entry_requirements: programme.entry_requirements || '',
        duration: programme.duration || '',
        credits: programme.credits || '',
        facilitator_name: programme.facilitator_name || '',
        moderator_name: programme.moderator_name || '',
        start_date: programme.start_date || '',
        end_date: programme.end_date || '',
        structure_type: programme.structure_type || 'weeks',
        description: programme.description || '',
        is_published: programme.is_published || false
      });

      setPeriods(programme.periods || []);
      
      // Expand all periods by default
      const expanded = {};
      (programme.periods || []).forEach(p => {
        expanded[p.period_id] = true;
      });
      setExpandedPeriods(expanded);
    } catch (error) {
      toast.error('Failed to load programme');
      navigate('/programmes');
    } finally {
      setLoading(false);
    }
  };

  const handleMetadataChange = (field, value) => {
    setMetadata(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProgramme = async () => {
    setSaving(true);
    try {
      let currentProgrammeId = programmeId;

      if (isEditing) {
        // Update existing programme
        await axios.put(`${API}/programmes/${programmeId}`, metadata, { headers });
      } else {
        // Create new programme
        const res = await axios.post(`${API}/programmes`, metadata, { headers });
        currentProgrammeId = res.data.programme_id;
      }

      // Save periods and activities
      for (const period of periods) {
        if (period.isNew) {
          // Create new period
          const periodRes = await axios.post(
            `${API}/programmes/${currentProgrammeId}/periods`,
            {
              period_number: period.period_number,
              title: period.title,
              description: period.description
            },
            { headers }
          );
          
          const newPeriodId = periodRes.data.period_id;

          // Create activities for this period
          for (const activity of period.activities || []) {
            await axios.post(
              `${API}/programmes/${currentProgrammeId}/periods/${newPeriodId}/activities`,
              activity,
              { headers }
            );
          }
        }
      }

      toast.success(isEditing ? 'Programme updated' : 'Programme created');
      navigate(`/admin/programmes/${currentProgrammeId}/edit`);
      
      if (!isEditing) {
        // Reload to get proper IDs
        window.location.href = `/admin/programmes/${currentProgrammeId}/edit`;
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save programme');
    } finally {
      setSaving(false);
    }
  };

  const addPeriod = () => {
    const newPeriodNumber = periods.length + 1;
    const structureLabel = metadata.structure_type === 'weeks' ? 'Week' : 'Month';
    const newPeriod = {
      period_id: `temp_${Date.now()}`,
      period_number: newPeriodNumber,
      title: `${structureLabel} ${newPeriodNumber}`,
      description: '',
      activities: [],
      isNew: true
    };
    setPeriods([...periods, newPeriod]);
    setExpandedPeriods(prev => ({ ...prev, [newPeriod.period_id]: true }));
  };

  const removePeriod = async (periodId, isNew) => {
    if (!window.confirm('Delete this period and all its activities?')) return;

    if (!isNew && programmeId) {
      try {
        await axios.delete(`${API}/programmes/${programmeId}/periods/${periodId}`, { headers });
      } catch (error) {
        toast.error('Failed to delete period');
        return;
      }
    }

    setPeriods(periods.filter(p => p.period_id !== periodId));
    toast.success('Period deleted');
  };

  const updatePeriod = (periodId, field, value) => {
    setPeriods(periods.map(p => 
      p.period_id === periodId ? { ...p, [field]: value } : p
    ));
  };

  const addActivity = (periodId) => {
    setPeriods(periods.map(p => {
      if (p.period_id === periodId) {
        const newActivity = {
          activity_id: `temp_${Date.now()}`,
          title: '',
          activity_type: 'course',
          content_id: '',
          external_url: '',
          document_url: '',
          description: '',
          order: (p.activities?.length || 0) + 1,
          is_required: true,
          allows_poe_submission: false,
          isNew: true
        };
        return { ...p, activities: [...(p.activities || []), newActivity] };
      }
      return p;
    }));
  };

  const removeActivity = async (periodId, activityId, isNew) => {
    if (!isNew && programmeId) {
      try {
        await axios.delete(`${API}/programmes/${programmeId}/activities/${activityId}`, { headers });
      } catch (error) {
        toast.error('Failed to delete activity');
        return;
      }
    }

    setPeriods(periods.map(p => {
      if (p.period_id === periodId) {
        return { ...p, activities: p.activities.filter(a => a.activity_id !== activityId) };
      }
      return p;
    }));
  };

  const updateActivity = (periodId, activityId, field, value) => {
    setPeriods(periods.map(p => {
      if (p.period_id === periodId) {
        return {
          ...p,
          activities: p.activities.map(a => 
            a.activity_id === activityId ? { ...a, [field]: value } : a
          )
        };
      }
      return p;
    }));
  };

  const togglePeriod = (periodId) => {
    setExpandedPeriods(prev => ({ ...prev, [periodId]: !prev[periodId] }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-zinc-800 rounded w-1/3" />
            <div className="h-64 bg-zinc-800 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/programmes')}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {isEditing ? 'Edit Programme' : 'Create Programme'}
              </h1>
              <p className="text-sm text-zinc-400">
                {isEditing ? 'Update programme details and structure' : 'Build a new professional programme'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={metadata.is_published}
                onCheckedChange={(checked) => handleMetadataChange('is_published', checked)}
              />
              <Label className="text-sm text-zinc-400">Published</Label>
            </div>
            <Button 
              onClick={handleSaveProgramme}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Programme'}
            </Button>
          </div>
        </div>

        {/* Programme Metadata */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white">Programme Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Row 1 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Programme Name</Label>
                <Input
                  value={metadata.programme_name}
                  onChange={(e) => handleMetadataChange('programme_name', e.target.value)}
                  placeholder="e.g., Advanced Compliance Professional"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Professional Body</Label>
                <Input
                  value={metadata.professional_body}
                  onChange={(e) => handleMetadataChange('professional_body', e.target.value)}
                  placeholder="e.g., CISI, ICA, SAICA"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400">Designation</Label>
                <Input
                  value={metadata.designation}
                  onChange={(e) => handleMetadataChange('designation', e.target.value)}
                  placeholder="e.g., Certified Compliance Officer"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Occupation</Label>
                <Input
                  value={metadata.occupation}
                  onChange={(e) => handleMetadataChange('occupation', e.target.value)}
                  placeholder="e.g., Compliance Manager"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">NQF Level</Label>
                <Input
                  value={metadata.nqf_level}
                  onChange={(e) => handleMetadataChange('nqf_level', e.target.value)}
                  placeholder="e.g., 5, 6, 7"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Target Audience</Label>
                <Input
                  value={metadata.target_audience}
                  onChange={(e) => handleMetadataChange('target_audience', e.target.value)}
                  placeholder="e.g., Compliance professionals, Risk managers"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Entry Requirements</Label>
                <Input
                  value={metadata.entry_requirements}
                  onChange={(e) => handleMetadataChange('entry_requirements', e.target.value)}
                  placeholder="e.g., Bachelor's degree or 3 years experience"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>

            {/* Row 4 */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-zinc-400">Duration</Label>
                <Input
                  value={metadata.duration}
                  onChange={(e) => handleMetadataChange('duration', e.target.value)}
                  placeholder="e.g., 12 weeks"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Credits</Label>
                <Input
                  value={metadata.credits}
                  onChange={(e) => handleMetadataChange('credits', e.target.value)}
                  placeholder="e.g., 120"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Facilitator Name</Label>
                <Input
                  value={metadata.facilitator_name}
                  onChange={(e) => handleMetadataChange('facilitator_name', e.target.value)}
                  placeholder="Facilitator name"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Moderator Name</Label>
                <Input
                  value={metadata.moderator_name}
                  onChange={(e) => handleMetadataChange('moderator_name', e.target.value)}
                  placeholder="Moderator name"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>

            {/* Row 5 - Dates and Structure */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400">Start Date</Label>
                <Input
                  type="date"
                  value={metadata.start_date}
                  onChange={(e) => handleMetadataChange('start_date', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">End Date</Label>
                <Input
                  type="date"
                  value={metadata.end_date}
                  onChange={(e) => handleMetadataChange('end_date', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Structure Type</Label>
                <Select
                  value={metadata.structure_type}
                  onValueChange={(value) => handleMetadataChange('structure_type', value)}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="weeks">Weekly Structure</SelectItem>
                    <SelectItem value="months">Monthly Structure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-zinc-400">Description</Label>
              <Textarea
                value={metadata.description}
                onChange={(e) => handleMetadataChange('description', e.target.value)}
                placeholder="Programme description..."
                rows={3}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Programme Structure */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">
              Programme Structure ({metadata.structure_type === 'weeks' ? 'Weeks' : 'Months'})
            </h2>
            <Button 
              onClick={addPeriod}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add {metadata.structure_type === 'weeks' ? 'Week' : 'Month'}
            </Button>
          </div>

          {periods.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800 border-dashed">
              <CardContent className="py-12 text-center">
                <Calendar className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 mb-4">
                  No {metadata.structure_type} added yet. Start building your programme structure.
                </p>
                <Button 
                  onClick={addPeriod}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First {metadata.structure_type === 'weeks' ? 'Week' : 'Month'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {periods.map((period, periodIndex) => (
                <Card key={period.period_id} className="bg-zinc-900 border-zinc-800">
                  <Collapsible
                    open={expandedPeriods[period.period_id]}
                    onOpenChange={() => togglePeriod(period.period_id)}
                  >
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-zinc-800/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <GripVertical className="h-5 w-5 text-zinc-600" />
                            <div>
                              <CardTitle className="text-white text-lg">
                                {period.title || `${metadata.structure_type === 'weeks' ? 'Week' : 'Month'} ${period.period_number}`}
                              </CardTitle>
                              <p className="text-sm text-zinc-400">
                                {period.activities?.length || 0} activities
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                removePeriod(period.period_id, period.isNew);
                              }}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            {expandedPeriods[period.period_id] ? (
                              <ChevronUp className="h-5 w-5 text-zinc-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-zinc-400" />
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-0 space-y-4">
                        {/* Period Details */}
                        <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-800/50 rounded-lg">
                          <div>
                            <Label className="text-zinc-400 text-sm">Period Title</Label>
                            <Input
                              value={period.title}
                              onChange={(e) => updatePeriod(period.period_id, 'title', e.target.value)}
                              placeholder={`${metadata.structure_type === 'weeks' ? 'Week' : 'Month'} ${period.period_number}`}
                              className="bg-zinc-800 border-zinc-700 text-white mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-sm">Description (optional)</Label>
                            <Input
                              value={period.description || ''}
                              onChange={(e) => updatePeriod(period.period_id, 'description', e.target.value)}
                              placeholder="Brief description of this period"
                              className="bg-zinc-800 border-zinc-700 text-white mt-1"
                            />
                          </div>
                        </div>

                        {/* Activities */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-zinc-300 font-medium">Activities</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addActivity(period.period_id)}
                              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Activity
                            </Button>
                          </div>

                          {(!period.activities || period.activities.length === 0) ? (
                            <div className="text-center py-6 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
                              <p className="text-sm text-zinc-500">No activities yet</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {period.activities.map((activity, activityIndex) => {
                                const activityType = ACTIVITY_TYPES.find(t => t.value === activity.activity_type);
                                const hasContentBuilder = activityType?.hasContentBuilder || false;
                                
                                return (
                                <div 
                                  key={activity.activity_id}
                                  className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700"
                                >
                                  <div className="grid grid-cols-12 gap-3">
                                    {/* Activity Type */}
                                    <div className="col-span-3">
                                      <Label className="text-zinc-400 text-xs">Type</Label>
                                      <Select
                                        value={activity.activity_type}
                                        onValueChange={(value) => updateActivity(period.period_id, activity.activity_id, 'activity_type', value)}
                                      >
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-800 border-zinc-700">
                                          {ACTIVITY_TYPES.map(type => (
                                            <SelectItem key={type.value} value={type.value}>
                                              {type.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    {/* Activity Title */}
                                    <div className="col-span-4">
                                      <Label className="text-zinc-400 text-xs">Title</Label>
                                      <Input
                                        value={activity.title}
                                        onChange={(e) => updateActivity(period.period_id, activity.activity_id, 'title', e.target.value)}
                                        placeholder="Activity title"
                                        className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"
                                      />
                                    </div>

                                    {/* Content Selection or URL - Only for non-builder types */}
                                    <div className="col-span-3">
                                      {['existing_course', 'masterclass'].includes(activity.activity_type) ? (
                                        <>
                                          <Label className="text-zinc-400 text-xs">Select Content</Label>
                                          <Select
                                            value={activity.content_id || ''}
                                            onValueChange={(value) => updateActivity(period.period_id, activity.activity_id, 'content_id', value)}
                                          >
                                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9">
                                              <SelectValue placeholder="Select..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-800 border-zinc-700 max-h-48">
                                              {activity.activity_type === 'existing_course' && availableCourses.map(c => (
                                                <SelectItem key={c.course_id} value={c.course_id}>
                                                  {c.title}
                                                </SelectItem>
                                              ))}
                                              {activity.activity_type === 'existing_lab' && availableLabs.map(l => (
                                                <SelectItem key={l.lab_id} value={l.lab_id}>
                                                  {l.title || l.name}
                                                </SelectItem>
                                              ))}
                                              {activity.activity_type === 'masterclass' && availableMasterclasses.map(m => (
                                                <SelectItem key={m.webinar_id} value={m.webinar_id}>
                                                  {m.title}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </>
                                      ) : activity.activity_type === 'existing_lab' ? (
                                        <>
                                          <Label className="text-zinc-400 text-xs">Select Lab</Label>
                                          <Select
                                            value={activity.content_id || ''}
                                            onValueChange={(value) => updateActivity(period.period_id, activity.activity_id, 'content_id', value)}
                                          >
                                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9">
                                              <SelectValue placeholder="Select..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-800 border-zinc-700 max-h-48">
                                              {availableLabs.map(l => (
                                                <SelectItem key={l.lab_id} value={l.lab_id}>
                                                  {l.title || l.name}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </>
                                      ) : activity.activity_type === 'aicc' ? (
                                        <>
                                          <Label className="text-zinc-400 text-xs">External/AICC URL (iframe embed)</Label>
                                          <Input
                                            value={activity.external_url || ''}
                                            onChange={(e) => updateActivity(period.period_id, activity.activity_id, 'external_url', e.target.value)}
                                            placeholder="https://omo.oldmutualcompliance.net/?aicc_embed=..."
                                            className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"
                                          />
                                          <p className="text-xs text-zinc-500 mt-1">This URL will be embedded in an iframe for learners</p>
                                        </>
                                      ) : activity.activity_type === 'video' || activity.activity_type === 'masterclass' ? (
                                        <>
                                          <Label className="text-zinc-400 text-xs">Video/Embed URL (iframe)</Label>
                                          <Input
                                            value={activity.external_url || ''}
                                            onChange={(e) => updateActivity(period.period_id, activity.activity_id, 'external_url', e.target.value)}
                                            placeholder="YouTube/Vimeo/Embed URL"
                                            className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"
                                          />
                                          <p className="text-xs text-zinc-500 mt-1">Video will be embedded in an iframe for learners</p>
                                        </>
                                      ) : activity.activity_type === 'document' || activity.activity_type === 'assignment_brief' ? (
                                        <div className="space-y-2">
                                          <Label className="text-zinc-400 text-xs">
                                            {activity.activity_type === 'assignment_brief' ? 'Assignment Brief Document' : 'Document'} (PDF/DOCX)
                                          </Label>
                                          {activity.document_filename ? (
                                            <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded border border-zinc-700">
                                              <FileText className="h-4 w-4 text-teal-400" />
                                              <span className="text-sm text-white flex-1 truncate">{activity.document_filename}</span>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => updateActivity(period.period_id, activity.activity_id, 'document_filename', null)}
                                                className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          ) : (
                                            <div className="relative">
                                              <input
                                                type="file"
                                                accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls"
                                                onChange={async (e) => {
                                                  const file = e.target.files?.[0];
                                                  if (file && programmeId) {
                                                    const formData = new FormData();
                                                    formData.append('file', file);
                                                    try {
                                                      const res = await axios.post(
                                                        `${API}/programmes/${programmeId}/activities/${activity.activity_id}/upload-document`,
                                                        formData,
                                                        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
                                                      );
                                                      updateActivity(period.period_id, activity.activity_id, 'document_filename', res.data.filename);
                                                      updateActivity(period.period_id, activity.activity_id, 'document_id', res.data.document_id);
                                                      toast.success('Document uploaded');
                                                    } catch (err) {
                                                      toast.error('Upload failed');
                                                    }
                                                  }
                                                }}
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                              />
                                              <div className="flex items-center gap-2 p-2 border border-dashed border-zinc-600 rounded hover:border-teal-500 cursor-pointer">
                                                <Upload className="h-4 w-4 text-zinc-400" />
                                                <span className="text-sm text-zinc-400">Click to upload document</span>
                                              </div>
                                            </div>
                                          )}
                                          <p className="text-xs text-zinc-500">
                                            {activity.activity_type === 'assignment_brief' 
                                              ? 'Learners will download this brief and submit their work as POE'
                                              : 'Learners can download this document'}
                                          </p>
                                        </div>
                                      ) : hasContentBuilder ? (
                                        <div className="flex items-end">
                                          <Badge className="bg-amber-500/20 text-amber-400 h-9 flex items-center">
                                            <Sparkles className="h-3 w-3 mr-1" />
                                            Build Content Below
                                          </Badge>
                                        </div>
                                      ) : (
                                        <>
                                          <Label className="text-zinc-400 text-xs">Description</Label>
                                          <Input
                                            value={activity.description || ''}
                                            onChange={(e) => updateActivity(period.period_id, activity.activity_id, 'description', e.target.value)}
                                            placeholder="Brief description"
                                            className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"
                                          />
                                        </>
                                      )}
                                    </div>

                                    {/* POE Toggle & Delete */}
                                    <div className="col-span-2 flex items-end gap-2">
                                      <div className="flex items-center gap-1">
                                        <Switch
                                          checked={activity.allows_poe_submission}
                                          onCheckedChange={(checked) => updateActivity(period.period_id, activity.activity_id, 'allows_poe_submission', checked)}
                                          className="scale-75"
                                        />
                                        <Label className="text-zinc-400 text-xs">POE</Label>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeActivity(period.period_id, activity.activity_id, activity.isNew)}
                                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9 w-9 p-0"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* Inline Content Builder for course/learning_lab types */}
                                  {hasContentBuilder && (
                                    <ActivityContentBuilder
                                      activity={activity}
                                      onUpdate={(updatedActivity) => {
                                        setPeriods(periods.map(p => {
                                          if (p.period_id === period.period_id) {
                                            return {
                                              ...p,
                                              activities: p.activities.map(a =>
                                                a.activity_id === activity.activity_id ? updatedActivity : a
                                              )
                                            };
                                          }
                                          return p;
                                        }));
                                      }}
                                      token={token}
                                      programmeId={programmeId}
                                    />
                                  )}
                                </div>
                              )})}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Save Button (Bottom) */}
        <div className="flex justify-end pt-6 border-t border-zinc-800">
          <Button 
            onClick={handleSaveProgramme}
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Programme'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
